import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useDeleteGroupMutation } from "../../infrastructure/api/membersGroupsApi";
import { useGroupDeletionCheck } from "../../application/hooks/useGroupDeletionCheck";
import type { Group, Member } from "../../domain/entities/Member";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
}

/**
 * Confirm → check members → delete. If any member belongs only to this
 * group, deletion is blocked and those members are listed instead.
 */
export const DeleteGroupDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  group,
}) => {
  const { t } = useTranslation("membersGroups");
  const [deleteGroup, { isLoading: deleting }] = useDeleteGroupMutation();
  const { findMembersOnlyInGroup, checking } = useGroupDeletionCheck();
  const [blockingMembers, setBlockingMembers] = useState<Member[] | null>(null);

  const busy = checking || deleting;

  const handleOpenChange = (next: boolean) => {
    if (busy) return;
    if (!next) setBlockingMembers(null);
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    if (!group) return;

    let onlyInGroup: Member[];
    try {
      onlyInGroup = await findMembersOnlyInGroup(group.id);
    } catch {
      toast.error(t("deleteGroupDialog.checkError"));
      return;
    }
    if (onlyInGroup.length > 0) {
      setBlockingMembers(onlyInGroup);
      return;
    }

    try {
      await deleteGroup(group.id).unwrap();
      toast.success(t("deleteGroupDialog.success", { name: group.name }));
      onOpenChange(false);
    } catch {
      toast.error(t("deleteGroupDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[480px]">
        {blockingMembers ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">
                {t("deleteGroupDialog.blockedTitle")}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 text-sm text-gray-500">
              <p>{t("deleteGroupDialog.blockedMessage")}</p>
              <ul className="flex flex-wrap gap-2">
                {blockingMembers.map((member) => (
                  <li
                    key={member.id}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-gray-900"
                  >
                    {member.name}
                  </li>
                ))}
              </ul>
              <p>{t("deleteGroupDialog.blockedHint")}</p>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={() => handleOpenChange(false)}
                className="cursor-pointer px-6 py-5"
              >
                {t("deleteGroupDialog.okay")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">
                {t("deleteGroupDialog.title")}
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-500">
                {t("deleteGroupDialog.confirm", { name: group?.name ?? "" })}
              </p>
            </DialogHeader>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={busy}
                className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
              >
                {t("deleteGroupDialog.cancel")}
              </Button>
              <Button
                onClick={() => void handleConfirm()}
                loading={busy}
                className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("deleteGroupDialog.delete")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
